/**
 * Upgrade wizard component for zero-config mode
 * Guides users through setting up full features
 */

import React, { useState, useEffect } from 'react';

export interface UpgradeStep {
  title: string;
  description?: string;
  command?: string;
  code?: string;
  time?: string;
  action?: () => void | Promise<void>;
}

export interface UpgradeWizardProps {
  steps: UpgradeStep[];
  onComplete?: () => void;
  onCancel?: () => void;
  title?: string;
  className?: string;
}

export function UpgradeWizard({
  steps,
  onComplete,
  onCancel,
  title = 'Enable Full Features',
  className = '',
}: UpgradeWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [isRunning, setIsRunning] = useState(false);
  const [copiedCommand, setCopiedCommand] = useState<string | null>(null);

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const allStepsCompleted = completedSteps.size === steps.length;

  const handleStepComplete = async () => {
    setCompletedSteps(prev => new Set([...prev, currentStep]));
    
    if (currentStepData.action) {
      setIsRunning(true);
      try {
        await currentStepData.action();
      } finally {
        setIsRunning(false);
      }
    }

    if (isLastStep) {
      onComplete?.();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleCopyCommand = (command: string) => {
    navigator.clipboard.writeText(command);
    setCopiedCommand(command);
    setTimeout(() => setCopiedCommand(null), 2000);
  };

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center bg-black/50 ${className}`}>
      <div className="w-full max-w-2xl bg-white dark:bg-gray-900 rounded-lg shadow-xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {title}
            </h2>
            {onCancel && (
              <button
                onClick={onCancel}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Progress */}
        <div className="px-6 py-3 bg-gray-50 dark:bg-gray-800">
          <div className="flex items-center space-x-2">
            {steps.map((step, index) => (
              <div key={index} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    completedSteps.has(index)
                      ? 'bg-green-500 text-white'
                      : index === currentStep
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {completedSteps.has(index) ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    index + 1
                  )}
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`w-12 h-0.5 ${
                      completedSteps.has(index)
                        ? 'bg-green-500'
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Step {currentStep + 1}: {currentStepData.title}
          </h3>
          
          {currentStepData.description && (
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {currentStepData.description}
            </p>
          )}

          {currentStepData.time && (
            <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-4">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Estimated time: {currentStepData.time}
            </div>
          )}

          {currentStepData.command && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Run this command:
              </label>
              <div className="relative">
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-md font-mono text-sm overflow-x-auto">
                  {currentStepData.command}
                </pre>
                <button
                  onClick={() => handleCopyCommand(currentStepData.command!)}
                  className="absolute top-2 right-2 px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-white rounded"
                >
                  {copiedCommand === currentStepData.command ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          )}

          {currentStepData.code && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Add this code:
              </label>
              <div className="relative">
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-md font-mono text-sm overflow-x-auto">
                  <code>{currentStepData.code}</code>
                </pre>
                <button
                  onClick={() => handleCopyCommand(currentStepData.code!)}
                  className="absolute top-2 right-2 px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-white rounded"
                >
                  {copiedCommand === currentStepData.code ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            <div className="flex items-center gap-3">
              {onCancel && (
                <button
                  onClick={onCancel}
                  className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                >
                  Cancel
                </button>
              )}
              
              <button
                onClick={handleStepComplete}
                disabled={isRunning}
                className="px-4 py-2 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRunning ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Running...
                  </span>
                ) : isLastStep ? (
                  allStepsCompleted ? 'Complete' : 'Finish'
                ) : (
                  'Next'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Quick setup wizard for common upgrade paths
 */
export function QuickSetupWizard({ onComplete, onCancel }: { onComplete?: () => void; onCancel?: () => void }) {
  const steps: UpgradeStep[] = [
    {
      title: 'Create API Route',
      description: 'First, we\'ll create an API route to enable server-side features.',
      command: 'npx vibeship init --minimal',
      time: '30 seconds',
    },
    {
      title: 'Restart Development Server',
      description: 'Restart your server to load the new API routes.',
      command: 'npm run dev',
      time: '10 seconds',
    },
    {
      title: 'All Set!',
      description: 'Your Vibeship DevTools now has full functionality. You can edit files, use AI features, and more!',
      action: async () => {
        // Wait a moment before reloading
        await new Promise(resolve => setTimeout(resolve, 1000));
        if (onComplete) {
          onComplete();
        } else {
          window.location.reload();
        }
      },
    },
  ];

  return (
    <UpgradeWizard
      title="Enable Full Features"
      steps={steps}
      onComplete={onComplete}
      onCancel={onCancel}
    />
  );
}